import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const zoomIn = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1.1);
  }
`;

const WhatWeDoContainer = styled.div`
  padding-top: 0;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, rgba(26, 77, 128, 0.8) 0%, rgba(44, 62, 80, 0.7) 100%), url('/IMG-20250802-WA0073.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  color: ${props => props.theme.colors.white};
  padding: 120px 0 80px;
  text-align: center;
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  animation: ${zoomIn} 10s ease-in-out infinite;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    background-attachment: scroll;
    min-height: 50vh;
    padding: 100px 0 60px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Section = styled.section`
  padding: 80px 0;
  margin-bottom: 0rem !important;
  padding-bottom: 0rem !important;
  background: #ffffff;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled(motion.h1)`
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: #f39c12;
    margin: 20px auto 0;
    border-radius: 0;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const WhatWeDoText = styled.span`
  display: block;
  color: #000000;
  line-height: 1.8;
  margin-bottom: 40px;
  font-size: 1.1rem;
  text-align: justify;
  font-family: 'Times New Roman', Times, serif;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
    text-align: left;
  }
`;

const ServiceRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PolaroidCard = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0,0,0,0.15);
  }
`;

const ServiceImage = styled.div`
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  ${PolaroidCard}:hover & img {
    transform: scale(1.05);
  }
`;

const ServiceContent = styled.div`
  padding: 20px;
  text-align: center;
  
  p {
    margin: 0;
    
    a {
      color: ${props => props.theme.colors.primary};
      text-decoration: none;
      font-weight: 600;
      font-size: 1.2rem;
      transition: color 0.3s ease;
      
      &:hover {
        color: ${props => props.theme.colors.secondary};
      }
    }
  }
`;

const EquipmentSection = styled.div`
  margin: 40px 0;
`;

const EquipmentTitle = styled.h5`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
  font-size: 1.2rem;
  font-weight: 600;
`;

const EquipmentList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 10px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const EquipmentItem = styled.li`
  color: #000000;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  font-size: 1rem;
  
  &:before {
    content: '✓';
    color: ${props => props.theme.colors.secondary};
    font-weight: bold;
    margin-right: 10px;
  }
`;

const BottomText = styled.div`
  background: rgba(26, 77, 128, 0.1);
  padding: 20px;
  border-radius: 0;
  margin: 30px 0;
  border: 1px solid #e0e0e0;
  
  span {
    color: #000000;
    line-height: 1.6;
    font-size: 1rem;
    
    b {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const WhatWeDo = () => {
  const services = [
    {
      image: '/IMG-20250802-WA0055.jpg',
      title: 'Events',
      link: '#'
    },
    {
      image: '/IMG-20250802-WA0056.jpg',
      title: 'Exhibitions',
      link: '#'
    },
    {
      image: '/IMG-20250802-WA0021.jpg',
      title: 'German Hangers',
      link: '#'
    },
    {
      image: '/confernce.jpg',
      title: 'Conferences',
      link: '#'
    },
    {
      image: '/IMG-20250802-WA0059.jpg',
      title: 'Weddings',
      link: '#'
    },
    {
      image: '/IMG-20250802-WA0060.jpg',
      title: 'Warehousing',
      link: '#'
    }
  ];

  const equipmentList = [
    'German Hangers for Rent in Bangalore',
    'Chair for Rent/ for hire  in Bangalore',
    'Furniture for rent',
    'Barricading for rent',
    'Backdrops for rent',
    'Carpets for rent',
    'China Tents for rent',
    'Chairs for Rent in Bangalore',
    'Canopy for Rent in Bangalore',
    'German Tent for rent',
    'Vessels Banquet Chairs  for rent ',
    'Shamiana for Rent in Bangalore',
    'Arabian Tents for rent ',
    'Sheet Sheds Tents for rent ',
    'Exhibition Stalls for Rent in Bangalore',
    'Aluminium Tents Bangalore',
    'Super Structures for Rent in Bangalore',
    'Stages made of both wood & steel ',
    'Steel tubular sheds for warehousing ',
    'Pandals for Rent in Bangalore',
    'Waterproof Sheds for Rent in Bangalore'
  ];

  return (
    <WhatWeDoContainer>
      <SEO
        title="Book Event Equipments & Tent Rentals Bangalore | Trimurthy Tent House"
        description="Hire German tents, aluminium tents, pagoda tents, canopies and event structures in Bangalore from Trimurthy Tent House. Call 88808 28698."
        canonical="https://ttheventorganisers.in/what-we-do"
      />
      <HeroSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{ color: '#ffffff' }}>What We Do</h1>
            <p style={{ 
              fontSize: '1.5rem', 
              opacity: 0.9, 
              maxWidth: '600px', 
              margin: '20px auto 0',
              color: '#ffffff',
              fontWeight: 600,
              textShadow: '1px 1px 6px rgba(0,0,0,0.5)',
              fontFamily: "'Times New Roman', Times, serif"
            }}>
              Transforming Dreams into Reality with Expert Tent Solutions & Event Excellence
            </p>
          </motion.div>
        </Container>
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            What We Do
          </SectionTitle>
          
          <WhatWeDoText>
            Trimurthy Tent House is a trusted 36-year old company specializing in premium German Hangers and comprehensive event management services. We provide event management services & supply materials for events, Exhibitions, conferences, conventions, concerts, weddings and all types of function in Bangalore & Pan India. Trimurthy Tent House are specialized in German Hangers, German tents, Aluminium tents, AC tents, Plywood platforms, Air conditioners, pagoda tents, Octornm exhibition stalls, super structures, mega hangers, wooden stages, pandals, and German hanger tents, Aluminium Hanger tents, waterproof sheds, shamiyana tents, chairs, canopies, 2 seater white leather sofas, furnitures etc on hire. Our German Hangers are particularly popular for large-scale corporate events, trade shows, and exhibitions requiring spacious, professional tent structures.
          </WhatWeDoText>

          <ServiceRow>
            {services.slice(0, 3).map((service, index) => (
              <PolaroidCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <ServiceImage>
                  <img src={service.image} alt={service.title} title={service.title} />
                </ServiceImage>
                <ServiceContent>
                  <p><a href={service.link}>{service.title}</a></p>
                </ServiceContent>
              </PolaroidCard>
            ))}
          </ServiceRow>

          <ServiceRow>
            {services.slice(3, 6).map((service, index) => (
              <PolaroidCard
                key={index + 3}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: (index + 3) * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <ServiceImage>
                  <img src={service.image} alt={service.title} title={service.title} />
                </ServiceImage>
                <ServiceContent>
                  <p><a href={service.link}>{service.title}</a></p>
                </ServiceContent>
              </PolaroidCard>
            ))}
          </ServiceRow>

          <EquipmentSection>
            <EquipmentTitle>
              Contact Trimurthy Tent House for the following equipments on Hire / for rent in Bangalore.
            </EquipmentTitle>
            <EquipmentList>
              {equipmentList.map((item, index) => (
                <EquipmentItem key={index}>{item}</EquipmentItem>
              ))}
            </EquipmentList>
          </EquipmentSection>

          <BottomText>
            <span><b>Trimurthy Tent House</b> - Aluminum Tents, German Tents, Canopies, Pandals, Shamianas, Exhibition Stalls, Super Structures, Mega Hangers, AC Tents, Wooden Stages, Waterproof Sheds and more for Rent and on Hire in Bangalore, Karnataka.</span>
          </BottomText>
        </Container>
      </Section>
    </WhatWeDoContainer>
  );
};

export default WhatWeDo; 